import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';
import { selectAgentOrgID } from 'app/modules/session/selectors';
import { selectHashedIdRoutesEnabled } from 'app/shared/featureFlags/selectors';

/**
 * Computes the delta-lake hash id for a CGDO external_id when the
 * CM_DELTA_LAKE_HASHED_ID_ROUTES flag is on. The hash is computed identically
 * for every CGDO type (entity, instrument, txn event, action event), so a
 * single hook covers all of them.
 *
 * Returns an empty `hashedId` (and `useHashed === false`) when the flag is off
 * or no external_id is provided, so callers can fall back to the legacy
 * external_id route.
 */
export const useHashedId = (
  externalId: string,
): { hashedId: string; useHashed: boolean } => {
  const orgId = useSelector(selectAgentOrgID);
  const hashedIdRoutesEnabled = useSelector(selectHashedIdRoutesEnabled);
  const hashedId = useMemo(
    () =>
      hashedIdRoutesEnabled && externalId
        ? computeDlHashIdForCgdo(orgId, externalId)
        : '',
    [hashedIdRoutesEnabled, externalId, orgId],
  );
  return { hashedId, useHashed: Boolean(hashedId) };
};
